/**
 * Coach profile (bio) schema + server functions.
 *
 * Aliases: coach profile, bio, profile edit, ProfileSchema
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireCoachAuth } from "~/lib/coach-middleware";
import {
  type SocialPlatform,
  normalizeSocialUrl,
  normalizeWebsiteUrl,
} from "~/lib/shared/social-links";

const UrlSchema = z.union([z.literal(""), z.string().url()], {
  errorMap: () => ({
    message:
      "Please enter a valid URL (e.g. https://example.com), or leave it blank.",
  }),
});

/**
 * External URLs supplied by coaches: normalize bare hosts (e.g. `example.com`
 * or `www.example.com`) to absolute URLs at the validation boundary so
 * downstream renderers can trust the value. Without this, anchors render a
 * relative href that browsers and SEO crawlers resolve against the current
 * page — producing 404 internal links flagged by SEO tooling.
 */
const ExternalUrlSchema = z
  .string()
  .transform(normalizeWebsiteUrl)
  .pipe(UrlSchema);

/**
 * Platform-aware variant for social fields. A coach who types a bare handle
 * (`jane.doe`) or a scheme-less URL (`instagram.com/jane`) gets it resolved to
 * an absolute profile URL on the right domain, so the saved value is always a
 * working external link rather than a 404-ing relative one.
 */
const socialUrlSchema = (platform: SocialPlatform) =>
  z
    .string()
    .transform((value) => normalizeSocialUrl(value, platform))
    .pipe(UrlSchema);

/**
 * Optional free-text field that is either blank or within [min, max] characters.
 * Replaces `z.union([z.literal(""), z.string().min().max()])`, which validates
 * identically but reports Zod's opaque "Invalid input" on failure — leaving
 * coaches with an error they can't act on. Same accept/reject behaviour, but
 * with a message that says exactly what to do.
 */
const boundedText = ({ min = 0, max }: { min?: number; max: number }) =>
  z
    .string()
    .max(max, `Please use ${max} characters or fewer.`)
    .refine((value) => value.length === 0 || value.length >= min, {
      message: `Please use at least ${min} characters, or leave it blank.`,
    });

/**
 * Image / upload URLs are produced by upload flows and data migrations (and can
 * be re-pointed when we move where images are hosted) — they are NOT typed by
 * hand. Validating them with a strict `.url()` means a single legacy or moved
 * value blocks the *entire* profile from saving, and on fields like the hero /
 * main image (which render through an uploader, not a text input) that failure
 * has no visible inline error — so the coach just sees "save failed" forever
 * with nothing to fix. Accept any stored string instead: a bad value renders as
 * a broken image the coach can re-upload, never an invisible save block. This
 * matches the other image fields that were already relaxed (e.g. struggles /
 * program / component images, certification images).
 */
const StoredImageUrl = z.string();

export const ProfileSchema = z.object({
  id: z.string(),
  coachId: z.string(),

  isPublic: z.boolean(),
  searchIndexingAllowed: z.boolean(),

  profilePictureUrl: StoredImageUrl.nullable(),

  brandName: z.string().max(50).nullable().optional(),
  brandLogoUrl: StoredImageUrl.nullable().optional(),

  coachingTitle: boundedText({ min: 3, max: 50 }).nullable(),
  languages: z.string().nullable(),
  coachingStyle: z.string().nullable(),
  formOfConduct: z.string().nullable(),
  audienceFor: boundedText({ max: 60 }).nullable().optional(),
  typicalEngagement: boundedText({ max: 30 }).nullable().optional(),
  // Read the year at parse time: Workers report the Unix epoch while a module's
  // top level runs, so a bound computed here would reject every year after 1970.
  coachingSinceYear: z
    .number()
    .int()
    .min(1950)
    .refine((year) => year <= new Date().getUTCFullYear(), {
      message: "Please enter a year no later than this year.",
    })
    .nullable()
    .optional(),

  openingStatement: z.string().max(100).nullable(),
  heroImageSrc: StoredImageUrl.nullable().optional(),
  mainImage: StoredImageUrl.nullable().optional(),
  mainVideo: UrlSchema.nullable().optional(),

  title: boundedText({ min: 3, max: 150 }).nullable(),
  description: boundedText({ min: 3, max: 400 }).nullable(),

  whyTitle: z.string().max(100).nullable(),
  whyContent: z.string().max(500).nullable(),

  challengesHeadline: boundedText({ min: 3, max: 100 }).nullable().optional(),
  challengesDescription: boundedText({ min: 3, max: 400 })
    .nullable()
    .optional(),

  outcomesHeadline: boundedText({ min: 3, max: 100 }).nullable().optional(),
  outcomesDescription: boundedText({ min: 3, max: 400 }).nullable().optional(),

  website: ExternalUrlSchema.nullable(),
  facebookProfile: socialUrlSchema("facebook").nullable(),
  linkedinProfile: socialUrlSchema("linkedin").nullable(),
  instagramProfile: socialUrlSchema("instagram").nullable(),
  youtubeChannel: socialUrlSchema("youtube").nullable(),

  ctaButtonText: boundedText({ min: 1, max: 50 }).nullable(),
  contactMode: z.enum(["booking", "messaging"]).nullable().optional(),
  ghlCalendarId: z.string().nullable().optional(),

  results: z
    .array(
      z.object({
        id: z.string().optional(),
        _delete: z.boolean().optional(),
        icon: z.string().min(1),
        title: z.string().min(1),
        description: z.string().nullable(),
        tempId: z.string().optional(),
      }),
    )
    .optional()
    .default([]),

  leadMagnetTitle: z.string().max(100).nullable(),
  leadMagnetDescription: z.string().max(500).nullable(),
  leadMagnetCtaText: z.string().max(50).nullable(),
  leadMagnetDownloadCtaText: z.string().max(50).nullable(),
  leadMagnetUrl: ExternalUrlSchema.nullable(),
});

export const getCoachProfile = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { createCoachProfile, updateCoach } =
      await import("~/lib/server/coach");

    const coachId = context.coach.id;

    if (!coachId) {
      throw new Error("Coach ID is required");
    }

    const fullCoach = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: {
        id: coachId,
      },
      with: {
        profile: true,
        results: {
          orderBy: (results, { asc }) => asc(results.order),
        },
      },
    });

    if (!fullCoach) {
      throw new Error("Coach not found");
    }

    let profile;
    if (fullCoach.profile) {
      profile = fullCoach.profile;
    } else {
      console.warn("No profile found for coach, creating empty", fullCoach.id);
      profile = await createCoachProfile({
        coachId: fullCoach.id,
      });
      await updateCoach(fullCoach.id, { profileId: profile.id });
    }

    return {
      ...profile,
      profilePictureUrl: fullCoach.profilePictureUrl,
      ghlCalendarId: fullCoach.ghlCalendarId,
      results: fullCoach.results.map((r) => ({
        id: r.id,
        icon: r.icon,
        title: r.title,
        description: r.description,
        _delete: false,
        tempId: `existing-${r.id}`,
      })),
    };
  });

export const updateCoachProfileAction = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(ProfileSchema)
  .handler(async ({ data, context }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { saveCoachProfile } =
      await import("~/lib/server/coach-profile-save");

    const { ghlCalendarId } = data;

    if (ghlCalendarId) {
      if (!context.coach.ghlLocationId) {
        throw new Error(
          "Cannot select a booking calendar without a connected GHL location.",
        );
      }

      const [{ getLocationAccessToken }, { getCalendar }] = await Promise.all([
        import("~/lib/ghl/get-location-access-token"),
        import("~/lib/ghl/get-calendars"),
      ]);
      const accessToken = await getLocationAccessToken(
        context.coach.ghlLocationId,
      );
      const { calendar } = await getCalendar(accessToken, ghlCalendarId);

      if (calendar.locationId !== context.coach.ghlLocationId) {
        throw new Error("Selected calendar does not belong to this coach.");
      }
      if (calendar.isActive === false) {
        throw new Error("Selected calendar is not active.");
      }
    }

    // The save works on the signed-in coach and ignores the form's `id` and
    // `coachId`.
    await saveCoachProfile(context.coach, data);

    const fullCoach = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: {
        id: context.coach.id,
      },
      with: {
        profile: true,
        results: {
          orderBy: (results, { asc }) => asc(results.order),
        },
      },
    });

    if (!fullCoach) {
      throw new Error("Coach not found");
    }

    if (!fullCoach.profile) {
      throw new Error("Coach profile not found");
    }

    const returnData = {
      ...fullCoach.profile,
      profilePictureUrl: fullCoach.profilePictureUrl,
      ghlCalendarId: fullCoach.ghlCalendarId,
      results: fullCoach.results.map((r) => ({
        id: r.id,
        icon: r.icon,
        title: r.title,
        description: r.description,
        _delete: false,
        tempId: `existing-${r.id}`,
      })),
    };

    return returnData;
  });
